import React from 'react';
import { Link } from 'react-router-dom';
import { fmtPrice } from '../../utils/pricing';
import { MAX_ADDON_QTY } from '../../utils/addons';
import './AccessoryPicker.css';

// Optional accessories as a grid of cards: photo, name, price and a quantity
// stepper. An accessory that comes free with the product shows "Included
// free" in place of the stepper - it is already on the order.
//
// Used by both configuring surfaces (the product-page widget and
// /configure/:slug), which pass the add-on state from useAwningConfig. The
// host owns the heading; this component owns the cards.
//
// `productName` is the short name used in "Included with your AW01".

function Stepper({ name, qty, onChange }) {
  return (
    <div className="addon-stepper">
      <button
        type="button"
        className="addon-stepper-btn"
        onClick={() => onChange(qty - 1)}
        disabled={qty <= 0}
        aria-label={`Remove one ${name}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14" strokeLinecap="round" /></svg>
      </button>
      <output className="addon-stepper-qty" aria-live="polite" aria-label={`${name} quantity`}>{qty}</output>
      <button
        type="button"
        className="addon-stepper-btn"
        onClick={() => onChange(qty + 1)}
        disabled={qty >= MAX_ADDON_QTY}
        aria-label={`Add one ${name}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
}

export default function AccessoryPicker({ options, quantities, onChange, productName }) {
  if (!options || !options.length) return null;
  return (
    <ul className="addon-grid">
      {options.map((opt) => {
        const qty = quantities?.[opt.slug] || 0;
        const chosen = opt.included || qty > 0;
        return (
          <li key={opt.slug} className={`addon-card${chosen ? ' addon-card--chosen' : ''}`}>
            {opt.included && <span className="addon-badge">Included</span>}
            <div className="addon-img">
              {opt.image
                ? <img src={opt.image} alt={opt.name} loading="lazy" width={240} height={180} />
                : <span className="addon-img-empty" aria-hidden="true" />}
            </div>
            <p className="addon-name">
              <Link to={`/products/${opt.slug}`} className="addon-name-link">{opt.name}</Link>
            </p>
            {opt.included ? (
              <>
                <p className="addon-price">
                  <span className="addon-price-free">FREE</span>
                  {opt.price != null && <span className="addon-price-value">${fmtPrice(opt.price)} value</span>}
                </p>
                <p className="addon-included">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M5 12l5 5 9-11" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Already added{productName ? ` to your ${productName}` : ''}
                </p>
              </>
            ) : (
              <>
                <p className="addon-price">${fmtPrice(opt.price)}</p>
                <Stepper name={opt.name} qty={qty} onChange={(q) => onChange(opt.slug, q)} />
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
